import { useEffect, useRef } from 'react';

import type { ReaderState } from '../types';
import { PageImage } from '../components/PageImage';
import { pageImageTransformFromRef } from '../pageTransforms';
import { cn } from '@/lib/utils';

export interface SpreadViewProps {
  state: ReaderState;
  onPageVisible?: (page: number) => void;
}

/**
 * Desktop 2-up. Renders pages `currentPage` and `currentPage + 1` (or
 * `currentPage - 1` in RTL) side by side. Page-advance moves by 2.
 *
 * If the user lands on the last odd page, we render a single page on one
 * side and a placeholder on the other.
 *
 * Spread mode is meant for desktop; on a narrow viewport it visually
 * degrades but still works.
 */
export function SpreadView({ state, onPageVisible }: SpreadViewProps) {
  const { pages, currentPage, prefs, resolveUrl, invalidateUrl } = state;
  const totalPages = pages.length;
  const onPageVisibleRef = useRef(onPageVisible);
  onPageVisibleRef.current = onPageVisible;

  useEffect(() => {
    onPageVisibleRef.current?.(currentPage);
  }, [currentPage]);

  /**
   * Choose the page pair. We always render the lower-numbered page as
   * "left" / "right" depending on direction, so the user sees a stable
   * order regardless of how they jumped in.
   */
  const left = currentPage;
  const right = currentPage + 1 <= totalPages ? currentPage + 1 : null;

  const [first, second] = prefs.direction === 'rtl' ? [right, left] : [left, right];

  return (
    <div className="flex h-[calc(100vh-7rem)] w-full select-none items-stretch justify-center gap-1 overflow-hidden bg-background px-1">
      <PagePane page={first} pages={pages} resolveUrl={resolveUrl} invalidateUrl={invalidateUrl} fit={prefs.fit} />
      <PagePane page={second} pages={pages} resolveUrl={resolveUrl} invalidateUrl={invalidateUrl} fit={prefs.fit} />
    </div>
  );
}

interface PagePaneProps {
  page: number | null;
  pages: ReaderState['pages'];
  resolveUrl: ReaderState['resolveUrl'];
  invalidateUrl: ReaderState['invalidateUrl'];
  fit: ReaderState['prefs']['fit'];
}

function PagePane({ page, pages, resolveUrl, invalidateUrl, fit }: PagePaneProps) {
  if (page === null) {
    return <div className="flex-1" aria-hidden />;
  }
  const ref = pages[page - 1];
  return (
    <div className="flex flex-1 items-center justify-center">
      <PageImage
        key={page}
        page={page}
        transform={pageImageTransformFromRef(ref)}
        resolveUrl={() => resolveUrl(page).then((u) => u ?? undefined)}
        onRetry={() => invalidateUrl(page)}
        className={cn(
          'h-full max-w-full',
          fit === 'width' && 'h-auto w-full',
          fit === 'height' && 'h-full w-auto',
        )}
      />
    </div>
  );
}
